import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, RefreshCw, Search, Printer, Download, Plus, Ban, UtensilsCrossed, CalendarDays, Presentation } from "lucide-react";
import { AdminShell } from "../components/AdminShell";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";

const TABS = [
  { id: "dinner", label: "Viernes — Cena", icon: UtensilsCrossed },
  { id: "general_event", label: "Sábado — Evento general", icon: CalendarDays },
  { id: "workshops", label: "Sábado — Talleres", icon: Presentation },
];

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—");

const AdminAsistencia = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("dinner");
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [workshops, setWorkshops] = useState([]);
  const [workshopId, setWorkshopId] = useState("");
  const [wsDetail, setWsDetail] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "", date: "", usher_id: "" });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ attendee_id: "", reason: "" });
  const [manualSearch, setManualSearch] = useState("");
  const [manualResults, setManualResults] = useState([]);
  const [voidTarget, setVoidTarget] = useState(null);
  const [dinnerStats, setDinnerStats] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [acting, setActing] = useState(false);
  const timerRef = useRef(null);

  const canWrite = user && ["admin", "welcome_coordinator"].includes(user.role);
  const canExport = user?.role === "admin";

  const loadSummary = useCallback(() => {
    api.get("/admin/attendance/summary").then((r) => { setSummary(r.data.checkpoints); setDinnerStats(r.data.dinner_stats || null); }).catch(() => {});
    api.get("/admin/attendance/workshops").then((r) => setWorkshops(r.data.workshops || [])).catch(() => {});
  }, []);

  const loadRows = useCallback((pg = page) => {
    setLoading(true);
    const params = { checkpoint_type: tab === "workshops" ? "workshop" : tab, page: pg, page_size: 25 };
    if (tab === "workshops") {
      if (!workshopId) { setRows([]); setTotal(0); setLoading(false); return; }
      params.workshop_id = workshopId;
    }
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/admin/attendance/list", { params })
      .then((r) => { setRows(r.data.rows || []); setTotal(r.data.total || 0); setLastUpdate(new Date()); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [tab, workshopId, filters, page]);

  useEffect(() => {
    const token = localStorage.getItem("hz_admin_token");
    if (!token) { navigate("/login"); return; }
    api.get("/auth/me")
      .then((r) => {
        if (!["admin", "welcome_coordinator", "viewer"].includes(r.data.role)) { navigate("/login"); return; }
        setUser(r.data);
      })
      .catch(() => { localStorage.removeItem("hz_admin_token"); navigate("/login"); });
  }, [navigate]);

  useEffect(() => { if (user) { loadSummary(); } }, [user, loadSummary]);
  useEffect(() => { if (user) loadRows(1); }, [user, tab, workshopId, filters]);

  useEffect(() => {
    if (!user) return;
    timerRef.current = setInterval(() => { loadSummary(); loadRows(page); }, 30000);
    return () => clearInterval(timerRef.current);
  }, [user, page, loadRows, loadSummary]);

  const refresh = () => { loadSummary(); loadRows(page); };

  const searchAttendees = async (q) => {
    setManualSearch(q);
    if (q.trim().length < 2) { setManualResults([]); return; }
    try {
      const { data } = await api.get("/admin/attendees", { params: { q } });
      setManualResults((data.attendees || []).slice(0, 8));
    } catch { /* silencioso */ }
  };

  const submitManual = async () => {
    if (acting) return;
    setActing(true);
    try {
      await api.post("/admin/attendance/manual", {
        attendee_id: manualForm.attendee_id,
        checkpoint_type: tab === "workshops" ? "workshop" : tab,
        workshop_id: tab === "workshops" ? workshopId : null,
        reason: manualForm.reason,
      });
      toast.success("Asistencia manual registrada y auditada.");
      setManualOpen(false);
      setManualForm({ attendee_id: "", reason: "" });
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setActing(false);
    }
  };

  const submitVoid = async () => {
    if (acting || !voidTarget) return;
    setActing(true);
    try {
      await api.post(`/admin/attendance/${voidTarget.checkin_id}/void`, { reason: voidReason });
      toast.success("Check-in anulado (corrección auditada).");
      setVoidTarget(null);
      setVoidReason("");
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setActing(false);
    }
  };

  const doExport = async (format) => {
    try {
      const params = { checkpoint_type: tab === "workshops" ? "workshop" : tab, format };
      if (tab === "workshops" && workshopId) params.workshop_id = workshopId;
      const { data: blob } = await api.get("/admin/attendance/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `asistencia_${params.checkpoint_type}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const cpSummary = summary?.[tab];
  const totalPages = Math.max(Math.ceil(total / 25), 1);

  if (!user) {
    return <div className="flex min-h-screen items-center justify-center bg-hz-bg"><Loader2 className="h-8 w-8 animate-spin text-hz-navy" /></div>;
  }

  return (
    <AdminShell roles={["admin", "welcome_coordinator", "viewer"]} title="Tablero de asistencia" testId="asistencia-page"
      subtitle="Control de check-ins de cena, evento general y talleres. Sin datos de pagos ni de menores.">
      {() => (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Puntos de control">
              {TABS.map((t) => (
                <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => { setTab(t.id); setPage(1); setFilters({ q: "", status: "", date: "", usher_id: "" }); }}
                  data-testid={`asistencia-tab-${t.id}`}
                  className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors ${tab === t.id ? "bg-hz-navy text-white" : "bg-white text-hz-navy border border-hz-gray/50"}`}>
                  <t.icon className="h-3.5 w-3.5" /> {t.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-hz-ink/60" data-testid="asistencia-last-update">
                {lastUpdate ? `Actualizado ${lastUpdate.toLocaleTimeString("es-MX")}` : ""}
              </span>
              <Button variant="outline" size="sm" onClick={refresh} data-testid="asistencia-refresh-btn" className="font-semibold">
                <RefreshCw className="mr-1 h-4 w-4" /> Refrescar
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="asistencia-print-btn" className="font-semibold">
                <Printer className="mr-1 h-4 w-4" /> Imprimir
              </Button>
              {canExport && (
                <>
                  <Button variant="outline" size="sm" onClick={() => doExport("csv")} data-testid="asistencia-export-csv" className="font-semibold"><Download className="mr-1 h-4 w-4" />CSV</Button>
                  <Button variant="outline" size="sm" onClick={() => doExport("xlsx")} data-testid="asistencia-export-xlsx" className="font-semibold"><Download className="mr-1 h-4 w-4" />XLSX</Button>
                </>
              )}
              {canWrite && (
                <Button size="sm" onClick={() => setManualOpen(true)} data-testid="asistencia-manual-btn"
                  disabled={tab === "workshops" && !workshopId}
                  className="bg-hz-cyan font-bold text-white hover:bg-hz-blue">
                  <Plus className="mr-1 h-4 w-4" /> Asistencia manual
                </Button>
              )}
            </div>
          </div>

          {tab !== "workshops" && cpSummary && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="asistencia-metrics">
              <div className="rounded-xl border border-hz-gray/40 bg-white p-4"><p className="text-xs font-semibold text-hz-ink/60">Autorizados</p><p className="text-2xl font-extrabold text-hz-navy" data-testid="metric-authorized">{cpSummary.authorized}</p></div>
              <div className="rounded-xl border border-hz-gray/40 bg-white p-4"><p className="text-xs font-semibold text-hz-ink/60">Presentes</p><p className="text-2xl font-extrabold text-hz-success" data-testid="metric-present">{cpSummary.present}</p></div>
              <div className="rounded-xl border border-hz-gray/40 bg-white p-4"><p className="text-xs font-semibold text-hz-ink/60">Ausentes</p><p className="text-2xl font-extrabold text-hz-warning" data-testid="metric-absent">{cpSummary.absent}</p></div>
              <div className="rounded-xl border border-hz-gray/40 bg-white p-4"><p className="text-xs font-semibold text-hz-ink/60">% Asistencia</p><p className="text-2xl font-extrabold text-hz-cyan" data-testid="metric-pct">{cpSummary.pct}%</p></div>
            </div>
          )}

          {tab === "dinner" && dinnerStats && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="dinner-capacity-panel">
              <div className="rounded-xl border border-hz-gray/40 bg-white p-3"><p className="text-xs font-semibold text-hz-ink/60">Capacidad cena</p><p className="text-xl font-extrabold text-hz-navy" data-testid="dinner-panel-capacity">{dinnerStats.capacity ?? "Sin límite"}</p></div>
              <div className="rounded-xl border border-hz-gray/40 bg-white p-3"><p className="text-xs font-semibold text-hz-ink/60">Confirmados</p><p className="text-xl font-extrabold text-hz-success" data-testid="dinner-panel-confirmed">{dinnerStats.confirmed}</p></div>
              <div className="rounded-xl border border-hz-gray/40 bg-white p-3"><p className="text-xs font-semibold text-hz-ink/60">Reservados pendientes</p><p className="text-xl font-extrabold text-hz-warning" data-testid="dinner-panel-reserved">{dinnerStats.reserved}</p></div>
              <div className="rounded-xl border border-hz-gray/40 bg-white p-3"><p className="text-xs font-semibold text-hz-ink/60">En lista de espera</p><p className="text-xl font-extrabold text-hz-cyan" data-testid="dinner-panel-waitlisted">{dinnerStats.waitlisted}</p></div>
              <div className="rounded-xl border border-hz-gray/40 bg-white p-3"><p className="text-xs font-semibold text-hz-ink/60">Disponibles</p><p className="text-xl font-extrabold text-hz-navy" data-testid="dinner-panel-available">{dinnerStats.available ?? "—"}</p></div>
              <div className="rounded-xl border border-hz-gray/40 bg-white p-3"><p className="text-xs font-semibold text-hz-ink/60">Sobre-cupo</p><p className="text-xl font-extrabold text-hz-error" data-testid="dinner-panel-over">{dinnerStats.over_capacity}</p></div>
            </div>
          )}

          {tab === "workshops" && !wsDetail && (
            <div className="space-y-3" data-testid="asistencia-workshops-summary">
              {workshops.length === 0 && !loading && <p className="text-sm text-hz-ink/60">No hay talleres configurados.</p>}
              {workshops.map((w) => (
                <button key={w.id} onClick={() => { setWorkshopId(w.id); setWsDetail(w); setPage(1); }}
                  data-testid={`asistencia-workshop-${w.id}`}
                  className="flex w-full flex-wrap items-center justify-between gap-3 rounded-xl border border-hz-gray/40 bg-white p-4 text-left hover:border-hz-cyan">
                  <div>
                    <p className="text-sm font-bold text-hz-navy">{w.name}</p>
                    <p className="text-xs text-hz-ink/60">Ponente: {w.speaker_name || "—"} · Salón: {w.responsible_name || "—"}</p>
                  </div>
                  <div className="flex gap-4 text-center text-xs">
                    <div><p className="font-extrabold text-hz-navy">{w.registered}</p><p className="text-hz-ink/60">Autorizados</p></div>
                    <div><p className="font-extrabold text-hz-success">{w.present}</p><p className="text-hz-ink/60">Presentes</p></div>
                    <div><p className="font-extrabold text-hz-warning">{w.absent}</p><p className="text-hz-ink/60">Ausentes</p></div>
                    <div><p className="font-extrabold text-hz-cyan">{w.pct}%</p><p className="text-hz-ink/60">Asistencia</p></div>
                    {w.available != null && <div><p className="font-extrabold text-hz-navy">{w.available}</p><p className="text-hz-ink/60">Disponibles</p></div>}
                  </div>
                </button>
              ))}
            </div>
          )}

          {tab === "workshops" && wsDetail && (
            <div className="flex items-center justify-between print:hidden">
              <Button variant="outline" size="sm" onClick={() => { setWsDetail(null); setWorkshopId(""); }} data-testid="asistencia-workshop-back" className="font-semibold">
                ← Todos los talleres
              </Button>
              <p className="text-sm font-bold text-hz-navy" data-testid="asistencia-workshop-title">{wsDetail.name}</p>
            </div>
          )}

          {(tab !== "workshops" || wsDetail) && (
            <>
              <div className="flex flex-wrap gap-2 print:hidden" data-testid="asistencia-filters">
                <form onSubmit={(e) => { e.preventDefault(); setPage(1); loadRows(1); }} className="flex gap-2">
                  <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Buscar folio o nombre" data-testid="asistencia-search" className="h-9 w-56 text-xs" />
                  <Button type="submit" variant="outline" size="sm" data-testid="asistencia-search-btn"><Search className="h-4 w-4" /></Button>
                </form>
                <select value={filters.status} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); }} data-testid="asistencia-filter-status" className="h-9 rounded-md border border-input bg-white px-2 text-xs">
                  <option value="">Presentes y ausentes</option>
                  <option value="present">Solo presentes</option>
                  <option value="absent">Solo ausentes</option>
                </select>
                <Input type="date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })} data-testid="asistencia-filter-date" className="h-9 w-40 text-xs" aria-label="Filtrar por fecha" />
              </div>

              {loading ? (
                <div className="flex items-center gap-2 py-8 text-hz-navy"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
              ) : rows.length === 0 ? (
                <p className="py-8 text-sm text-hz-ink/60" data-testid="asistencia-empty">Sin registros para los filtros seleccionados.</p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-hz-gray/40 bg-white">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-bold text-hz-navy">Folio</TableHead>
                        <TableHead className="font-bold text-hz-navy">Nombre</TableHead>
                        <TableHead className="font-bold text-hz-navy">Delegación · Sector</TableHead>
                        <TableHead className="font-bold text-hz-navy">Asistencia</TableHead>
                        <TableHead className="font-bold text-hz-navy">Hora de entrada</TableHead>
                        <TableHead className="font-bold text-hz-navy">Registrado por</TableHead>
                        <TableHead className="font-bold text-hz-navy">Método</TableHead>
                        {canWrite && <TableHead className="font-bold text-hz-navy print:hidden">Acciones</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow key={r.attendee_id} data-testid={`asistencia-row-${r.folio}`}>
                          <TableCell className="font-bold text-hz-navy">{r.folio}</TableCell>
                          <TableCell className="font-semibold">{r.full_name}</TableCell>
                          <TableCell className="text-xs">{[r.delegation_name, r.sector_name].filter(Boolean).join(" · ") || "—"}</TableCell>
                          <TableCell>
                            <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${r.attendance_status === "present" ? "bg-hz-success/10 text-hz-success" : "bg-hz-gray/30 text-hz-ink"}`} data-testid={`asistencia-status-${r.folio}`}>
                              {r.attendance_status === "present" ? "● Presente" : "○ Ausente"}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs">{fmtTime(r.checked_at)}</TableCell>
                          <TableCell className="text-xs">{r.usher_name || "—"}</TableCell>
                          <TableCell className="text-xs">{r.method || "—"}</TableCell>
                          {canWrite && (
                            <TableCell className="print:hidden">
                              {r.checkin_id && (
                                <Button variant="outline" size="sm" onClick={() => { setVoidTarget(r); setVoidReason(""); }} data-testid={`asistencia-void-${r.folio}`} className="text-xs font-bold text-hz-error">
                                  <Ban className="mr-1 h-3.5 w-3.5" /> Anular
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              <div className="flex items-center justify-between text-xs text-hz-ink/60 print:hidden">
                <span data-testid="asistencia-total">{total} registro(s) · página {page} de {totalPages}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage(page - 1); loadRows(page - 1); }} data-testid="asistencia-prev">Anterior</Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => { setPage(page + 1); loadRows(page + 1); }} data-testid="asistencia-next">Siguiente</Button>
                </div>
              </div>
            </>
          )}

          <Dialog open={manualOpen} onOpenChange={setManualOpen}>
            <DialogContent className="bg-white sm:max-w-md" aria-describedby={undefined}>
              <DialogHeader>
                <DialogTitle className="font-bold text-hz-navy" data-testid="manual-title">Asistencia manual — {TABS.find((t) => t.id === tab)?.label}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-semibold">Buscar asistente (folio o nombre)</Label>
                  <Input value={manualSearch} onChange={(e) => searchAttendees(e.target.value)} data-testid="manual-search" className="mt-1" />
                  {manualResults.length > 0 && !manualForm.attendee_id && (
                    <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-hz-gray/40">
                      {manualResults.map((a) => (
                        <button key={a.id} onClick={() => { setManualForm({ ...manualForm, attendee_id: a.id }); setManualSearch(`${a.folio} — ${a.full_name}`); setManualResults([]); }}
                          data-testid={`manual-pick-${a.folio}`}
                          className="block w-full px-3 py-2 text-left text-xs hover:bg-hz-bg">
                          <span className="font-bold text-hz-navy">{a.folio}</span> · {a.full_name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-semibold">Motivo obligatorio</Label>
                  <Textarea value={manualForm.reason} onChange={(e) => setManualForm({ ...manualForm, reason: e.target.value })} rows={2}
                    placeholder="Ej. El asistente no podía mostrar su QR…" data-testid="manual-reason" className="mt-1" />
                </div>
                <Button onClick={submitManual} disabled={acting || !manualForm.attendee_id || manualForm.reason.trim().length < 5}
                  data-testid="manual-submit" className="w-full bg-hz-cyan font-bold text-white hover:bg-hz-blue">
                  {acting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Registrar asistencia
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={!!voidTarget} onOpenChange={(o) => !o && setVoidTarget(null)}>
            <DialogContent className="bg-white sm:max-w-md" aria-describedby={undefined}>
              <DialogHeader>
                <DialogTitle className="font-bold text-hz-navy" data-testid="void-title">Anular check-in de {voidTarget?.folio}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <p className="text-xs text-hz-ink/70">La anulación no borra el registro: crea una corrección auditada y libera el acceso para una nueva entrada.</p>
                <Textarea value={voidReason} onChange={(e) => setVoidReason(e.target.value)} rows={2}
                  placeholder="Motivo obligatorio (mínimo 5 caracteres)" data-testid="void-reason" />
                <Button onClick={submitVoid} disabled={acting || voidReason.trim().length < 5} data-testid="void-submit"
                  className="w-full bg-hz-error font-bold text-white hover:bg-hz-error/90">
                  {acting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Anular asistencia
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </AdminShell>
  );
};

export default AdminAsistencia;
